import type {
  AlteracaoDeMarcacao,
  BaixaDePagamento,
  CancelamentoDeMarcacao,
  ConsultaDeDisponibilidade,
  DetalheDaMarcacao,
  EscopoDeAlteracao,
  GradeDoDia,
  HorasACobrar,
  MarcacaoCriada,
  NovaMarcacao,
  ProximasNaAgenda,
  ResultadoDeAlteracao,
  ResultadoDeDisponibilidade,
} from '@arena/shared/schemas';
import { api } from './cliente.js';

/**
 * As chamadas da agenda.
 *
 * A entrada é sempre DATA e HORA LOCAIS ("2026-09-15", "19:00"): quem
 * converte para instante é o servidor, com o fuso da arena. O navegador
 * nunca calcula um instante — e por isso o fuso do computador da
 * recepção não interfere em nada.
 *
 * Os tipos são os de `@arena/shared/schemas`, os mesmos que o backend
 * usa para validar e responder.
 */
export const agendaApi = {
  /** Sem data, o servidor responde "hoje" — no fuso da arena, não no do navegador. */
  grade(data?: string): Promise<GradeDoDia> {
    return api<GradeDoDia>(data ? `/agenda?data=${encodeURIComponent(data)}` : '/agenda');
  },

  /**
   * As horas de quadra a cobrar do período — sem datas, HOJE.
   *
   * Três telas chamam: o painel de hoje, o card do financeiro e o KPI
   * "Previsto de locação" da agenda.
   */
  aCobrar(periodo: { de?: string; ate?: string } = {}): Promise<HorasACobrar> {
    const busca = new URLSearchParams();
    if (periodo.de) busca.set('de', periodo.de);
    if (periodo.ate) busca.set('ate', periodo.ate);
    const query = busca.toString();
    return api<HorasACobrar>(`/agenda/a-cobrar${query ? `?${query}` : ''}`);
  },

  /**
   * Dar baixa no pagamento de uma marcação.
   *
   * Vai pelo MESMO `PATCH` da alteração, e não por uma rota própria: dar
   * baixa é corrigir a cobrança da marcação, e a trilha, o acerto do
   * razão e a pergunta da série já moram lá. `escopo` vem como
   * 'ocorrencia' quando a marcação é de série — pagar a aula de hoje não
   * paga as próximas onze semanas.
   */
  darBaixa(
    id: string,
    dados: BaixaDePagamento,
    escopo?: EscopoDeAlteracao,
  ): Promise<ResultadoDeAlteracao> {
    return api<ResultadoDeAlteracao>(`/agenda/marcacoes/${id}`, {
      metodo: 'PATCH',
      corpo: { ...dados, ...(escopo ? { escopo } : {}) },
    });
  },

  proximas(limite = 6): Promise<ProximasNaAgenda> {
    return api<ProximasNaAgenda>(`/agenda/proximas?limite=${limite}`);
  },

  disponibilidade(consulta: ConsultaDeDisponibilidade): Promise<ResultadoDeDisponibilidade> {
    return api<ResultadoDeDisponibilidade>('/agenda/disponibilidade', {
      metodo: 'POST',
      corpo: consulta,
    });
  },

  criar(dados: NovaMarcacao): Promise<MarcacaoCriada> {
    return api<MarcacaoCriada>('/agenda/marcacoes', { metodo: 'POST', corpo: dados });
  },

  detalhe(id: string): Promise<DetalheDaMarcacao> {
    return api<DetalheDaMarcacao>(`/agenda/marcacoes/${id}`);
  },

  alterar(id: string, dados: AlteracaoDeMarcacao): Promise<ResultadoDeAlteracao> {
    return api<ResultadoDeAlteracao>(`/agenda/marcacoes/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  cancelar(id: string, dados: CancelamentoDeMarcacao): Promise<ResultadoDeAlteracao> {
    return api<ResultadoDeAlteracao>(`/agenda/marcacoes/${id}/cancelar`, {
      metodo: 'POST',
      corpo: dados,
    });
  },
};
